import React from "react";

export type RiskLevel = "low" | "medium" | "high";
export type ReadinessLevel = "red" | "yellow" | "green";

interface HealthGaugeProps {
  level: RiskLevel | ReadinessLevel;
  type: "risk" | "readiness";
  size?: "sm" | "md" | "lg";
  showLabel?: boolean;
  onClick?: () => void;
  className?: string;
}

export const HealthGauge: React.FC<HealthGaugeProps> = ({
  level,
  type,
  size = "md",
  showLabel = true,
  onClick,
  className = "",
}) => {
  const sizeClasses = {
    sm: "w-12 h-12",
    md: "w-16 h-16",
    lg: "w-20 h-20",
  };

  const getColorClasses = () => {
    if (type === "risk") {
      switch (level as RiskLevel) {
        case "low":
          return {
            bg: "bg-green-100",
            border: "border-green-500",
            text: "text-green-700",
            needle: "bg-green-600",
            label: "Low Risk",
          };
        case "medium":
          return {
            bg: "bg-yellow-100",
            border: "border-yellow-500",
            text: "text-yellow-700",
            needle: "bg-yellow-600",
            label: "Medium Risk",
          };
        case "high":
          return {
            bg: "bg-red-100",
            border: "border-red-500",
            text: "text-red-700",
            needle: "bg-red-600",
            label: "High Risk",
          };
      }
    } else {
      switch (level as ReadinessLevel) {
        case "green":
          return {
            bg: "bg-green-100",
            border: "border-green-500",
            text: "text-green-700",
            needle: "bg-green-600",
            label: "Ready",
          };
        case "yellow":
          return {
            bg: "bg-yellow-100",
            border: "border-yellow-500",
            text: "text-yellow-700",
            needle: "bg-yellow-600",
            label: "In Progress",
          };
        case "red":
          return {
            bg: "bg-red-100",
            border: "border-red-500",
            text: "text-red-700",
            needle: "bg-red-600",
            label: "Not Ready",
          };
      }
    }
  };

  const colors = getColorClasses();

  const getNeedleRotation = () => {
    if (type === "risk") {
      switch (level as RiskLevel) {
        case "low":
          return "-45deg";
        case "medium":
          return "0deg";
        case "high":
          return "45deg";
      }
    } else {
      switch (level as ReadinessLevel) {
        case "red":
          return "-45deg";
        case "yellow":
          return "0deg";
        case "green":
          return "45deg";
      }
    }
  };

  return (
    <div
      className={`flex flex-col items-center ${onClick ? "cursor-pointer" : ""} ${className}`}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div
        className={`relative ${sizeClasses[size]} ${colors.bg} ${colors.border} border-4 rounded-full flex items-center justify-center overflow-hidden transition-all hover:scale-105`}
      >
        {/* Gauge segments */}
        <div className="absolute inset-0">
          <div
            className="absolute left-0 bottom-0 w-1/2 h-1/2 origin-bottom-right"
            style={{
              background:
                type === "risk"
                  ? "linear-gradient(135deg, rgba(34, 197, 94, 0.3) 0%, rgba(34, 197, 94, 0.1) 100%)"
                  : "linear-gradient(135deg, rgba(239, 68, 68, 0.3) 0%, rgba(239, 68, 68, 0.1) 100%)",
              transform: "rotate(-90deg)",
            }}
          ></div>
          <div
            className="absolute left-1/2 bottom-0 w-1/2 h-1/2 origin-bottom-left"
            style={{
              background:
                "linear-gradient(45deg, rgba(234, 179, 8, 0.3) 0%, rgba(234, 179, 8, 0.1) 100%)",
            }}
          ></div>
          <div
            className="absolute right-0 bottom-0 w-1/2 h-1/2 origin-bottom-left"
            style={{
              background:
                type === "risk"
                  ? "linear-gradient(315deg, rgba(239, 68, 68, 0.3) 0%, rgba(239, 68, 68, 0.1) 100%)"
                  : "linear-gradient(315deg, rgba(34, 197, 94, 0.3) 0%, rgba(34, 197, 94, 0.1) 100%)",
              transform: "rotate(90deg)",
            }}
          ></div>
        </div>

        {/* Needle */}
        <div
          className="absolute bottom-1/2 left-1/2 w-1 origin-bottom transition-transform duration-500"
          style={{
            height: "45%",
            transform: `translateX(-50%) rotate(${getNeedleRotation()})`,
            backgroundColor: colors.needle,
          }}
        >
          <div
            className={`absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full ${colors.needle}`}
          ></div>
        </div>

        {/* Center dot */}
        <div
          className={`absolute bottom-1/2 left-1/2 -translate-x-1/2 translate-y-1/2 w-3 h-3 rounded-full ${colors.needle} z-10`}
        ></div>
      </div>

      {showLabel && (
        <span className={`mt-1 text-xs font-medium ${colors.text}`}>
          {colors.label}
        </span>
      )}
    </div>
  );
};
